import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { parseHtml } from '@/utils/string-utils';
import { teaserText } from '@/utils/textService';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface ProseProps {
  content: string;
  maxLength?: number;
  className?: string;
  readMore?: boolean;
  proseSize?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl';
  textWrapMode?: 'wrap' | 'pretty' | 'balance';
  hyphenation?: boolean;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
}

const Prose: React.FC<ProseProps> = ({
  content,
  maxLength = 255,
  className = '',
  readMore = true,
  proseSize = 'base',
  textWrapMode = 'pretty',
  hyphenation = false,
  textAlign = 'left',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [teaserContent, setTeaserContent] = useState('');
  const [needsTruncation, setNeedsTruncation] = useState(false);

  useEffect(() => {
    if (content) {
      // For content length calculation, we use the content directly
      // If it contains HTML tags, the length will include them, but for truncation purposes this is acceptable
      const teaser = teaserText(content, maxLength);
      setTeaserContent(teaser);
      setNeedsTruncation(content.length > maxLength);
    }
  }, [content, maxLength]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!content) {
    return null;
  }

  const displayContent = isExpanded ? content : teaserContent;

  // Construire la classe prose avec la taille
  const proseClass = proseSize === 'base' ? 'prose' : `prose text-sm lg:text-${proseSize}`;

  // Classes pour la gestion du texte avec Tailwind CSS modernes
  const textClasses = cn(
    // Classes de base pour la typographie
    proseClass,
    'text-foreground [&>li]:mt-1 [&>p]:mb-5 [&>ul]:-mt-2 [&>ul]:mb-5 [&>ul]:list-disc',

    // Gestion moderne du wrapping avec Tailwind CSS
    textWrapMode === 'wrap' && 'text-wrap',
    textWrapMode === 'pretty' && 'text-pretty',
    textWrapMode === 'balance' && 'text-balance',

    // Césure automatique
    hyphenation ? 'hyphens-auto' : 'hyphens-none',

    // Alignement du texte
    textAlign === 'center' && 'text-center',
    textAlign === 'right' && 'text-right',
    textAlign === 'justify' && 'text-justify',
    textAlign === 'left' && 'text-left',

    // Classes pour améliorer la lisibilité
    'leading-relaxed tracking-normal break-words',

    // Empêcher la coupure inappropriée des éléments inline
    '[&>code]:break-normal [&>em]:break-normal [&>strong]:break-normal'
  );

  return (
    <div className={cn('prose-container')}>
      <div
        className={cn(
          'prose-content',
          `prose-${proseSize}`,
          textClasses,
          hyphenation ? 'hyphens-auto' : 'hyphens-none',
          textAlign === 'center' && 'text-center',
          textAlign === 'right' && 'text-right',
          textAlign === 'justify' && 'text-justify',
          textAlign === 'left' && 'text-left',
          'leading-relaxed',
          className
        )}
      >
        {/* Use parseHtml to render content with proper HTML entity handling */}
        {parseHtml(displayContent)}
        {needsTruncation && readMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleExpanded}
            className="text-xs cursor-zoom-in text-muted-foreground h-4 translate-y-1"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="mr-1 h-3 w-3" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="mr-1 h-3 w-3" />
                Read more
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Prose;
